import React, { useMemo, useState } from 'react';
import { Stack, Tabs, Tab, Box } from '@mui/material';
import { BasePage } from '../../components/base-page';
import { useQuery, useMutation } from '@apollo/client/react';
import { TaskDetailDialog } from '../../components/task-detail-dialog';
import {
  TaskListFragment,
  TaskStatus,
  CreateTaskDocument,
  CreateTaskMutation,
  CreateTaskMutationVariables,
  GetTasksDocument,
  GetTasksQuery,
  CreateRecurringTaskDocument,
  CreateRecurringTaskMutation,
  CreateRecurringTaskMutationVariables,
  UpdateRecurringTaskDocument,
  UpdateRecurringTaskMutation,
  UpdateRecurringTaskMutationVariables,
  GetRecurringTasksForAccountDocument,
  GetRecurringTasksForAccountQuery,
  RecurringTaskFragmentFragment,
} from '../../generated/graphql';
import { useSearchParams } from 'react-router';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import { useAlert } from '@dwelvio/react-alert';
import { NewTaskDialog } from '../../components/new-task-dialog';
import { NewRecurringTaskDialog } from '../../components/new-recurring-task-dialog';
import { RecurringTasksList } from '../../components/recurring-tasks-list';
import { useApp } from '../../components/app-context';
import { Button, Typography } from '@mui/material';
import { AddIcon } from '../../components/icons';
import { TaskStatusChip } from '../../components/task-status-chip';

const FilterArray = ['not_completed', 'next7', 'overdue', 'all', 'future'] as const;
type FilterType = (typeof FilterArray)[number];

const TabsArray = ['tasks', 'recurring'] as const;
type TabsType = (typeof TabsArray)[number];

export function startsAfterToday(startDate: string | null | undefined, now = new Date()) {
  if (!startDate) return false;

  const endOfToday = new Date(now);
  endOfToday.setHours(23, 59, 59, 999);

  return new Date(startDate).getTime() > endOfToday.getTime();
}

export function filterTasks(tasks: TaskListFragment[], filterGroup: FilterType, now = new Date()) {
  return tasks.filter((task) => {
    // Always exclude deleted tasks
    if (task.deletedAt) return false;
    const isCompleted = !!task.completedAt;
    const dueDate = task.dueDate ? new Date(task.dueDate).getTime() : 9999999999999;
    const nowTimestamp = now.getTime();
    const inNext7Days = !isCompleted && dueDate >= nowTimestamp && dueDate <= nowTimestamp + 7 * 24 * 60 * 60 * 1000;
    const startsAfterTodayTask = startsAfterToday(task.startDate, now);
    const isOverdue = !isCompleted && dueDate < nowTimestamp;

    if (filterGroup === 'not_completed' && (isCompleted || startsAfterTodayTask)) return false;
    if (filterGroup === 'next7' && !inNext7Days) return false;
    if (filterGroup === 'overdue' && !isOverdue) return false;
    if (filterGroup === 'future' && !startsAfterTodayTask) return false;

    return true;
  });
}

export function TaskListPage() {
  const app = useApp();
  const [searchParams, setSearchParams] = useSearchParams();
  const { show } = useAlert();
  const [showAddNewTask, setShowAddNewTask] = useState<boolean>(false);
  const [showAddNewRecurringTask, setShowAddNewRecurringTask] = useState<boolean>(false);
  const [editingRecurringTask, setEditingRecurringTask] = useState<RecurringTaskFragmentFragment | null>(null);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);

  let filterGroup: FilterType = (searchParams.get('fg') as FilterType) ?? 'not_completed';
  if (filterGroup && !FilterArray.includes(filterGroup as FilterType)) {
    searchParams.set('fg', 'not_completed');
    setSearchParams(searchParams);
  }

  let tabValue: TabsType = (searchParams.get('tv') as TabsType) ?? 'tasks';
  if (tabValue !== 'tasks' && tabValue !== 'recurring') {
    searchParams.set('tv', 'tasks');
    setSearchParams(searchParams);
  }

  const {
    data: taskData,
    refetch: refetchTasks,
    loading: tasksLoading,
    error: tasksError,
  } = useQuery<GetTasksQuery>(GetTasksDocument);

  const {
    data: recurringTaskData,
    refetch: refetchRecurringTasks,
    loading: recurringTasksLoading,
    error: recurringTasksError,
  } = useQuery<GetRecurringTasksForAccountQuery>(GetRecurringTasksForAccountDocument);

  React.useEffect(() => {
    if (tasksError) {
      show.error('Nepodarilo sa načítať úlohy.', tasksError.message);
    }
    if (recurringTasksError) {
      show.error('Nepodarilo sa načítať pravidelné úlohy.', recurringTasksError.message);
    }
  }, [tasksError, recurringTasksError, show]);

  const [createTask, { loading: createLoading }] = useMutation<CreateTaskMutation, CreateTaskMutationVariables>(
    CreateTaskDocument,
    {
      onError: (e: Error) => {
        show.error('Nepodarilo sa vytvoriť úlohu.', e.message);
      },
    },
  );

  const [createRecurringTask, { loading: createRecurringLoading }] = useMutation<
    CreateRecurringTaskMutation,
    CreateRecurringTaskMutationVariables
  >(CreateRecurringTaskDocument, {
    onError: (e: Error) => {
      show.error('Nepodarilo sa vytvoriť pravidelnú úlohu.', e.message);
    },
  });

  const [updateRecurringTask, { loading: updateRecurringLoading }] = useMutation<
    UpdateRecurringTaskMutation,
    UpdateRecurringTaskMutationVariables
  >(UpdateRecurringTaskDocument, {
    onError: (e: Error) => {
      show.error('Nepodarilo sa upraviť pravidelnú úlohu.', e.message);
    },
  });

  const tasks = React.useMemo(() => filterTasks(taskData?.getTasks ?? [], filterGroup), [taskData, filterGroup]);

  const columns = React.useMemo((): GridColDef<TaskListFragment>[] => {
    return [
      {
        display: 'flex',
        field: 'title',
        headerName: 'Názov',
        width: 250,
        renderCell: (t) => {
          const secondary = t.row.unit?.name ?? t.row.contract?.title ?? null;
          return (
            <Stack alignSelf={'center'}>
              <Typography>{t.value}</Typography>
              {secondary && (
                <Typography color="text.secondary" sx={{ fontSize: '0.8em' }}>
                  {secondary}
                </Typography>
              )}
            </Stack>
          );
        },
      },
      {
        field: 'dueDate',
        headerName: 'Termín splnenia',
        type: 'string',
        valueGetter: (value: string) => value ?? '9',
        display: 'flex',
        width: 100,
        renderCell: (params) => {
          const isOverdue = params.value && new Date(params.value) < new Date() && !params.row.completedAt;
          return (
            <Stack alignSelf={'center'}>
              <Typography color={isOverdue ? 'error' : 'inherit'}>
                {params.value === '9' ? '' : app.formatDate(params.value as string)}
              </Typography>
            </Stack>
          );
        },
        sortable: true,
      },
      {
        field: 'status',
        headerName: 'Stav',
        width: 110,
        renderCell: (params) => {
          return <TaskStatusChip status={params.value as TaskStatus} size="small" />;
        },
        sortable: true,
      },
      {
        field: 'lastComment',
        headerName: 'Posledný komentár',
        valueGetter: (value: string, row) => row.lastComment?.comment ?? '',
        width: 250,

        sortable: true,
      },
    ];
  }, [app]);

  return (
    <BasePage
      title="Úlohy"
      loading={tasksLoading || recurringTasksLoading}
      errorMsg={tasksError || recurringTasksError ? 'Nepodarilo na načítať úlohy.' : undefined}
    >
      <Stack direction="column" flexDirection="column" width="100%">
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h2">Úlohy</Typography>
          {tabValue === 'tasks' ? (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setShowAddNewTask(true)}>
              Pridať úlohu
            </Button>
          ) : (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setShowAddNewRecurringTask(true)}>
              Pridať pravidelnú úlohu
            </Button>
          )}
        </Stack>

        <Tabs
          value={tabValue}
          onChange={(e, newValue) => {
            searchParams.set('tv', newValue);
            setSearchParams(searchParams);
          }}
          sx={{ mb: 2 }}
        >
          <Tab label="Úlohy" value="tasks" />
          <Tab label="Pravidelné úlohy" value="recurring" />
        </Tabs>

        {tabValue === 'tasks' && (
          <>
            <Stack direction="row" spacing={2} sx={{ marginBottom: 2 }} flexWrap="wrap" useFlexGap>
              <Button
                variant={filterGroup != 'not_completed' ? 'outlined' : 'contained'}
                onClick={() => {
                  searchParams.set('fg', 'not_completed');
                  setSearchParams(searchParams);
                }}
              >
                Nesplnené
              </Button>
              <Button
                variant={filterGroup != 'next7' ? 'outlined' : 'contained'}
                onClick={() => {
                  searchParams.set('fg', 'next7');
                  setSearchParams(searchParams);
                }}
              >
                Nasledujúcich 7 dní
              </Button>
              <Button
                variant={filterGroup != 'future' ? 'outlined' : 'contained'}
                onClick={() => {
                  searchParams.set('fg', 'future');
                  setSearchParams(searchParams);
                }}
              >
                Budúce
              </Button>
              <Button
                variant={filterGroup != 'overdue' ? 'outlined' : 'contained'}
                onClick={() => {
                  searchParams.set('fg', 'overdue');
                  setSearchParams(searchParams);
                }}
              >
                Po termíne
              </Button>
              <Button
                variant={filterGroup != 'all' ? 'outlined' : 'contained'}
                onClick={() => {
                  searchParams.set('fg', 'all');
                  setSearchParams(searchParams);
                }}
              >
                Všetky
              </Button>
            </Stack>
            <DataGrid
              rows={tasks}
              columns={columns}
              initialState={{
                pagination: {
                  paginationModel: {
                    pageSize: 10,
                  },
                },
                sorting: {
                  sortModel: [{ field: 'dueDate', sort: 'asc' }],
                },
              }}
              pageSizeOptions={[10, 20, 50]}
              disableRowSelectionOnClick
              onRowClick={(params) => {
                setSelectedTaskId(params.row.id);
              }}
            />
          </>
        )}

        {tabValue === 'recurring' && (
          <RecurringTasksList
            tasks={recurringTaskData?.getRecurringTasksForAccount || []}
            onRefresh={() => refetchRecurringTasks()}
            onEdit={(task) => setEditingRecurringTask(task)}
            showSecondLine={true}
          />
        )}
      </Stack>

      {showAddNewTask && (
        <NewTaskDialog
          open={showAddNewTask}
          onClose={() => setShowAddNewTask(false)}
          onSubmit={(input) => {
            createTask({
              variables: { input: { ...input, status: 'todo' } },
              onCompleted: () => {
                setShowAddNewTask(false);
                refetchTasks();
              },
            });
          }}
          loading={createLoading}
        />
      )}

      {showAddNewRecurringTask && (
        <NewRecurringTaskDialog
          open={showAddNewRecurringTask}
          onClose={() => setShowAddNewRecurringTask(false)}
          onSubmit={(input) => {
            createRecurringTask({
              variables: { input },
              onCompleted: () => {
                setShowAddNewRecurringTask(false);
                refetchRecurringTasks();
              },
            });
          }}
          loading={createRecurringLoading}
        />
      )}

      {editingRecurringTask && (
        <NewRecurringTaskDialog
          open={true}
          editTask={editingRecurringTask}
          onClose={() => setEditingRecurringTask(null)}
          onSubmit={(input) => {
            updateRecurringTask({
              variables: { recurringTaskId: editingRecurringTask.id, input },
              onCompleted: () => {
                setEditingRecurringTask(null);
                refetchRecurringTasks();
              },
            });
          }}
          loading={updateRecurringLoading}
        />
      )}

      <TaskDetailDialog taskId={selectedTaskId} open={!!selectedTaskId} onClose={() => setSelectedTaskId(null)} />
    </BasePage>
  );
}
